import { useEffect, useRef, useState } from "react";

/**
 * <img> with a shimmer skeleton that covers the image until it is fully
 * decoded. Renders the <img> first and the skeleton AFTER as a sibling so
 * the skeleton paints ON TOP — that's what hides the browser's progressive
 * top-down decode of large JPEGs. We wait for `img.decode()` (not just the
 * `load` event) so the bitmap is in memory before we fade the skeleton out
 * — no half-images, no flash.
 *
 * Optional `fallbackSrc` prop: if the primary `src` 404s (typical when a
 * WebP variant hasn't been generated on the server yet), the component
 * swaps once to `fallbackSrc` and keeps the skeleton visible until that
 * loads. Lets the deploy survive a missing variant without showing
 * broken-image placeholders.
 *
 * Parent MUST be `position: relative` with a defined height (use an
 * `aspect-*` utility on the parent). Pass any standard <img> props;
 * `className` lands on the <img> so existing hover transforms keep working.
 */
export function ImageWithSkeleton({
  src,
  alt = "",
  onError,
  fallbackSrc,
  className = "",
  loading = "lazy",
  ...rest
}) {
  const [activeSrc, setActiveSrc] = useState(src);
  const [loaded, setLoaded] = useState(false);
  // Tracks whether we've already swapped to the fallback so a fallback
  // that also 404s doesn't loop us back to the primary.
  const triedFallbackRef = useRef(false);
  const imgRef = useRef(null);

  // When the caller-supplied src changes, reset everything.
  useEffect(() => {
    triedFallbackRef.current = false;
    setActiveSrc(src);
  }, [src]);

  useEffect(() => {
    setLoaded(false);
    const img = imgRef.current;
    if (!img || !activeSrc) return;
    let cancelled = false;
    const markLoaded = () => {
      if (!cancelled) setLoaded(true);
    };

    if (img.complete && img.naturalWidth > 0) {
      img.decode().then(markLoaded).catch(markLoaded);
      return () => {
        cancelled = true;
      };
    }

    const onLoad = () => {
      img.decode().then(markLoaded).catch(markLoaded);
    };
    const onErr = () => {
      // Only mark loaded if we won't be retrying with the fallback —
      // otherwise the skeleton would briefly fade out and back in.
      if (!fallbackSrc || triedFallbackRef.current) markLoaded();
    };
    img.addEventListener("load", onLoad);
    img.addEventListener("error", onErr);
    return () => {
      cancelled = true;
      img.removeEventListener("load", onLoad);
      img.removeEventListener("error", onErr);
    };
  }, [activeSrc, fallbackSrc]);

  const handleError = (e) => {
    if (fallbackSrc && !triedFallbackRef.current && activeSrc !== fallbackSrc) {
      triedFallbackRef.current = true;
      setActiveSrc(fallbackSrc);
      // Don't fade the skeleton yet — wait for the fallback to load.
      return;
    }
    setLoaded(true);
    onError?.(e);
  };

  return (
    <>
      <img
        ref={imgRef}
        src={activeSrc}
        alt={alt}
        loading={loading}
        onError={handleError}
        className={className}
        {...rest}
      />
      <div
        aria-hidden
        className={`pointer-events-none absolute inset-0 skeleton-shimmer transition-opacity duration-500 ${
          loaded ? "opacity-0" : "opacity-100"
        }`}
      />
    </>
  );
}
